import React, { useEffect, useMemo, useState } from 'react';
import { Carousel } from '@dotcom/crown-ui';
import { CardFaceToggle } from '../../components/card-face-toggle';
import { CardFacePreviewImage } from '../../global-types';
import styles from './card-preview.module.scss';
import { CardPreviewProps, CardPreviewState, CardPreviewSurface } from './card-preview.types';
import { getTransparentImageTrim, TransparentImageTrim } from './utils/get-transparent-image-trim';

const DEFAULT_SURFACE_WIDTH = 520;
const EMPTY_IMAGE_ALT = '';
const INITIAL_FACE_INDEX = 0;
const SURFACE_WIDTH_PRECISION = 1000;

/**
 * Gets the visible dimensions CardPreview should reserve for a surface.
 *
 * Full-spread inside panels reserve half the source width; S&S transparent
 * trims reserve only the opaque artwork bounds.
 *
 * @param surface Preview surface metadata
 * @param transparentTrim Optional transparent-edge trim computed after image load
 * @returns Dimensions used for aspect-ratio and sizing CSS variables
 */
const getRenderedDimensions = (
  surface: CardPreviewSurface,
  transparentTrim?: TransparentImageTrim,
): CardPreviewSurface['dimensions'] => {
  if (surface.crop) {
    return { height: surface.dimensions.height, width: surface.dimensions.width / 2 };
  }

  if (transparentTrim) {
    return {
      height: transparentTrim.sourceHeight - transparentTrim.top - transparentTrim.bottom,
      width: transparentTrim.sourceWidth - transparentTrim.left - transparentTrim.right,
    };
  }

  return surface.dimensions;
};

/**
 * Builds the image class list for normal and full-spread cropped surfaces.
 *
 * @param surface Preview surface metadata
 * @returns Space-separated CSS module classes for the image element
 */
const getSurfaceImageClass = (surface: CardPreviewSurface): string =>
  [
    styles['surface-image'],
    surface.crop ? styles['surface-image--crop'] : '',
    surface.crop === 'right' ? styles['surface-image--crop-right'] : '',
  ]
    .filter(Boolean)
    .join(' ');

/**
 * Builds CSS variables that size one preview surface.
 *
 * The reference surface keeps navigation between panels from causing large
 * perceived jumps, while each surface still keeps its own aspect ratio.
 *
 * @param surface Preview surface metadata
 * @param referenceSurface Surface used as the sizing reference
 * @param transparentTrim Optional transparent-edge trim computed after image load
 * @param targetSurfaceWidth Target rendered width for the reference surface
 * @param targetSurfaceHeight Optional maximum rendered height for the reference surface
 * @returns React style object with CardPreview CSS variables
 */
const getSurfaceStyle = (
  surface: CardPreviewSurface,
  referenceSurface: CardPreviewSurface | undefined,
  transparentTrim?: TransparentImageTrim,
  targetSurfaceWidth = DEFAULT_SURFACE_WIDTH,
  targetSurfaceHeight?: number,
): React.CSSProperties => {
  const dimensions = getRenderedDimensions(surface, transparentTrim);
  const referenceDimensions = referenceSurface ? getRenderedDimensions(referenceSurface) : dimensions;
  const referenceHeightFromWidth = (targetSurfaceWidth * referenceDimensions.height) / referenceDimensions.width;
  const referenceHeight =
    targetSurfaceHeight === undefined
      ? referenceHeightFromWidth
      : Math.min(referenceHeightFromWidth, targetSurfaceHeight);
  const rawSurfaceWidth = (referenceHeight * dimensions.width) / dimensions.height;
  const surfaceWidth = Math.min(Math.round(rawSurfaceWidth * SURFACE_WIDTH_PRECISION) / SURFACE_WIDTH_PRECISION, 1040);

  return {
    '--card-preview-aspect-ratio': `${dimensions.width} / ${dimensions.height}`,
    '--card-preview-surface-width': `${surfaceWidth}px`,
  } as React.CSSProperties;
};

/**
 * Converts transparent-edge trim metadata into image positioning styles.
 *
 * The stage is sized to the visible artwork; the image is enlarged and offset
 * so transparent source padding is clipped by the stage overflow.
 *
 * @param transparentTrim Transparent edge trim computed from image pixels
 * @returns Inline image style used to hide transparent padding
 */
const getTransparentTrimImageStyle = (transparentTrim: TransparentImageTrim | undefined): React.CSSProperties => {
  if (!transparentTrim) return {};

  const visibleWidth = transparentTrim.sourceWidth - transparentTrim.left - transparentTrim.right;
  const visibleHeight = transparentTrim.sourceHeight - transparentTrim.top - transparentTrim.bottom;

  return {
    height: `${(transparentTrim.sourceHeight / visibleHeight) * 100}%`,
    left: `${(-transparentTrim.left / visibleWidth) * 100}%`,
    top: `${(-transparentTrim.top / visibleHeight) * 100}%`,
    width: `${(transparentTrim.sourceWidth / visibleWidth) * 100}%`,
  };
};

/**
 * Builds optional workspace overrides used when preview is embedded in editor.
 *
 * @param targetSurfaceTop Top offset where the surface should render
 * @param targetSurfaceBottom Bottom offset preserved from the editor surface
 * @returns Inline style for the preview workspace
 */
const getWorkspaceStyle = (
  targetSurfaceTop: number | undefined,
  targetSurfaceBottom: number | undefined,
  resetPadding = false,
): React.CSSProperties | undefined => {
  const hasVerticalOffset = targetSurfaceTop !== undefined || targetSurfaceBottom !== undefined;
  if (!hasVerticalOffset && !resetPadding) return undefined;

  return {
    ...(hasVerticalOffset
      ? {
          alignItems: 'flex-start',
          paddingBottom: `${targetSurfaceBottom ?? 0}px`,
          paddingTop: `${targetSurfaceTop ?? 0}px`,
        }
      : {
          paddingBottom: 0,
          paddingTop: 0,
        }),
    paddingLeft: 0,
    paddingRight: 0,
  };
};

/**
 * Renders a single preview surface.
 *
 * This component owns per-image behavior, including S&S transparent-edge trim
 * after load, while the parent owns surface selection/navigation.
 *
 * @param props Surface render props
 * @returns Preview surface section
 */
const Surface = ({
  isActive,
  referenceSurface,
  showSurfaceChrome,
  surface,
  targetSurfaceHeight,
  targetSurfaceWidth,
}: {
  isActive: boolean;
  referenceSurface?: CardPreviewSurface;
  showSurfaceChrome: boolean;
  surface: CardPreviewSurface;
  targetSurfaceHeight?: number;
  targetSurfaceWidth?: number;
}): React.ReactElement => {
  const [transparentTrim, setTransparentTrim] = useState<TransparentImageTrim>();
  const isWiderThanReference =
    referenceSurface &&
    surface.dimensions.width / surface.dimensions.height >
      referenceSurface.dimensions.width / referenceSurface.dimensions.height;
  const surfaceClasses = [styles.surface, isWiderThanReference ? styles['surface--wide'] : '']
    .filter(Boolean)
    .join(' ');
  const shouldTrimTransparentEdges = Boolean(surface.trimTransparentEdges && !surface.crop && showSurfaceChrome);
  const imageStyle = getTransparentTrimImageStyle(transparentTrim);

  useEffect(() => {
    setTransparentTrim(undefined);
  }, [surface.dataURL]);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    if (!shouldTrimTransparentEdges) {
      return;
    }

    setTransparentTrim(getTransparentImageTrim(event.currentTarget));
  };

  return (
    <section
      aria-hidden={!isActive}
      className={surfaceClasses}
      data-testid={`card-preview-surface-${surface.type}`}
      style={getSurfaceStyle(surface, referenceSurface, transparentTrim, targetSurfaceWidth, targetSurfaceHeight)}
    >
      <div
        className={[styles['surface-stage'], showSurfaceChrome ? '' : styles['surface-stage--plain']]
          .filter(Boolean)
          .join(' ')}
        aria-label={`${surface.label} preview`}
      >
        <img
          className={getSurfaceImageClass(surface)}
          src={surface.dataURL}
          alt={EMPTY_IMAGE_ALT}
          crossOrigin={shouldTrimTransparentEdges && !surface.dataURL.startsWith('data:') ? 'anonymous' : undefined}
          draggable={false}
          loading="eager"
          onLoad={handleImageLoad}
          style={imageStyle}
        />
      </div>
    </section>
  );
};

/**
 * Adapts CardPreview surfaces into the shape expected by CardFaceToggle.
 *
 * @param previewState Current quick-preview state
 * @returns Navigation face list for the editor navigation component
 */
const getNavigationFaces = (previewState: CardPreviewState): CardFacePreviewImage[] =>
  previewState.surfaces.map((surface, index) => ({
    dimensions: { width: surface.dimensions.width, height: surface.dimensions.height },
    faceNumber: index + 1,
    photoZoneTemplate: surface.dataURL,
    type: surface.navigationLabel,
    url: surface.dataURL,
  }));

/**
 * Renders static card preview snapshots with optional editor-style navigation.
 *
 * CardPreview does not read Fabric canvases directly. It only renders the
 * snapshot state created by createCardPreviewState.
 *
 * @param props CardPreview render props
 * @returns Static quick-preview UI
 */
export const CardPreview = ({
  previewState,
  variant = 'standalone',
  zoomMode = 'fit',
  showNavigation = true,
  activeSurfaceIndex: controlledIndex,
  onSurfaceChange,
  targetSurfaceWidth,
  targetSurfaceHeight,
  targetSurfaceTop,
  targetSurfaceBottom,
  showSurfaceChrome = true,
  className,
  testId = 'card-preview',
}: CardPreviewProps): React.ReactElement => {
  const [internalIndex, setInternalIndex] = useState(INITIAL_FACE_INDEX);
  const activeSurfaceIndex = controlledIndex ?? internalIndex;
  const setActiveSurfaceIndex = (index: number) => {
    if (controlledIndex === undefined) setInternalIndex(index);
    onSurfaceChange?.(index);
  };
  const navigationFaces = useMemo(() => getNavigationFaces(previewState), [previewState]);
  const classes = [
    styles['card-preview'],
    zoomMode === 'actual' ? styles['card-preview--actual'] : '',
    showNavigation ? '' : styles['card-preview--without-navigation'],
    variant === 'embedded' ? styles['card-preview--embedded'] : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const referenceSurface =
    previewState.surfaces.find((surface) => surface.type === 'front') ?? previewState.surfaces[0];
  const activeSurface = previewState.surfaces.find((_surface, index) => index === activeSurfaceIndex);
  const sizingReferenceSurface =
    targetSurfaceWidth === undefined ? referenceSurface : activeSurface ?? referenceSurface;

  return (
    <div className={classes} data-testid={testId}>
      <div
        className={styles.workspace}
        style={getWorkspaceStyle(targetSurfaceTop, targetSurfaceBottom, variant === 'embedded')}
      >
        <div className={styles['surface-carousel-container']}>
          <Carousel
            addClass={styles['surface-carousel']}
            currentSlideIndex={activeSurfaceIndex}
            disableKeyboardControls={true}
            dragging={false}
            slideClass={styles['surface-slide']}
            slidesToScroll={1}
            slidesToShow={1}
          >
            {previewState.surfaces.map((surface, index) => (
              <Surface
                key={surface.id + surface.type}
                isActive={activeSurfaceIndex === index}
                referenceSurface={sizingReferenceSurface}
                showSurfaceChrome={showSurfaceChrome}
                surface={surface}
                targetSurfaceHeight={targetSurfaceHeight}
                targetSurfaceWidth={targetSurfaceWidth}
              />
            ))}
          </Carousel>
        </div>
      </div>
      {showNavigation && (
        <div className={styles.navigation}>
          <CardFaceToggle
            faces={navigationFaces}
            slideIndex={activeSurfaceIndex}
            handleIndexChange={setActiveSurfaceIndex}
            mode="preview"
            testId="card-preview-navigation"
          />
        </div>
      )}
    </div>
  );
};
